import type {MatchParams} from '@wandb/weave/common/types/base';

import type {ContentPart} from '../store/thread';
import {collectAgentPageContext} from './collect';

/**
 * PromptContextParams describes the user's current route scope (entity,
 * project, run) as captured by React Router's URL match. The runtime hook
 * consumes this on every user message to derive the wandb_web_page_state URL
 * and append the corresponding scope parts so the agent can ground its
 * response.
 */
export type PromptContextParams = MatchParams;

/**
 * buildPromptContextParts produces a small set of context content parts that
 * describe the user's current page state at send time. The page-state URL
 * is always included; entity/project/run parts are appended when the
 * corresponding nav params are present.
 */
export function buildPromptContextParts({
  entityName,
  projectName,
  runName,
}: PromptContextParams): ContentPart[] {
  const pageContent = collectAgentPageContext();
  const hasProject = !!(entityName && projectName);
  const parts: ContentPart[] = [
    {
      type: 'wandb-web-page-state',
      content: pageContent.length > 0 ? pageContent : undefined,
      entityName: entityName || undefined,
      projectName: hasProject ? projectName : undefined,
      // NOTE: Drop query/hash to limit prompt-injection and PII leakage.
      url: window.location.origin + window.location.pathname,
    },
  ];

  if (entityName) {
    parts.push({type: 'wandb-entity', entityName});
  }

  if (hasProject) {
    parts.push({
      type: 'wandb-project',
      entityName,
      projectName,
    });
  }

  if (hasProject && runName) {
    parts.push({
      type: 'wandb-run',
      entityName,
      projectName,
      runName,
      runDisplayNameStatus: 'pending',
    });
  }

  return parts;
}
